import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, ArrowRight, Heart, MapPin, Sprout } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import Reveal from '@/components/Reveal';
import PageHero from '@/components/PageHero';

const OurHomes = () => {
  return (
    <div className="relative z-10 min-h-screen">
      <Seo title="Our Homes" description="Forever Path Living™ is developing a network of relationship-centered supportive living homes in Virginia, beginning with The Chasnell Home™ in Richmond. Virginia DBHDS initial licensure in progress." />
      <PageHero
        eyebrow="A Network Built Thoughtfully"
        title="Our Homes"
        subtitle="Forever Path Living™ is developing homes designed to provide consistent, high-quality, relationship-centered support."
      />

      {/* Intro */}
      <section className="py-20 md:py-28 px-6 bg-cream-100">
        <Reveal className="max-w-3xl mx-auto text-center">
          <p className="text-lg md:text-xl text-forest-700 leading-relaxed mb-4">
            Forever Path Living™ is developing a network of residential homes designed to provide consistent,
            high-quality support environments.
          </p>
          <p className="text-forest-700 leading-relaxed">
            Each home operates within the Forever Path Living™ standard, and our organization intends to grow
            thoughtfully over time in response to community needs, organizational readiness, available resources,
            and future licensing.
          </p>
        </Reveal>
      </section>

      {/* First Home */}
      <section className="py-4 md:py-8 px-6 bg-cream-100">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-5xl text-forest-800 mb-4">Our First Home</h2>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-sage-100 border border-gold-500/30 rounded-full">
              <span className="w-2 h-2 rounded-full bg-gold-500" aria-hidden="true"></span>
              <p className="text-forest-800 font-medium text-sm">Now Welcoming Referral Inquiries — Virginia DBHDS Licensure In Progress</p>
            </div>
          </Reveal>

          <Reveal>
            <Card className="overflow-hidden border border-sage-200 rounded-3xl shadow-[0_16px_40px_rgba(59,77,65,0.08)]">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12 bg-forest-900 text-sage-100 flex flex-col justify-center">
                  <div className="inline-flex p-3 rounded-2xl bg-forest-800 text-gold-500 mb-5 w-fit"><Building2 className="w-7 h-7" /></div>
                  <h3 className="font-serif text-3xl text-cream-100 mb-3">The Chasnell Home™</h3>
                  <div className="flex items-center gap-2 text-sage-100/85 mb-6">
                    <MapPin className="w-4 h-4 text-gold-500" /> Richmond, Virginia
                  </div>
                  <p className="leading-relaxed text-sage-100/85">
                    Our initial residential program, currently being developed to serve adult females as part of our
                    initial Virginia DBHDS licensure strategy. This reflects our current operational plan — not the
                    limits of our long-term vision.
                  </p>
                </div>

                <div className="p-8 md:p-12 bg-cream-100">
                  <div className="space-y-5 mb-8">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-forest-700 mt-0.5" />
                      <p className="text-forest-700">A relationship-centered home for adults being supported with dignity and consistency.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-forest-700 mt-0.5" />
                      <p className="text-forest-700">Structured and emotionally regulated environment.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Sprout className="w-5 h-5 text-forest-700 mt-0.5" />
                      <p className="text-forest-700">Building relationships before opening our doors.</p>
                    </div>
                  </div>
                  <div className="p-4 bg-sage-100 border-l-4 border-gold-500 rounded-xl mb-6">
                    <p className="text-forest-800 text-sm">Referral inquiries are welcomed and reviewed on a rolling basis as we complete licensure. Not currently accepting admissions.</p>
                  </div>
                  <Link to="/chasnell">
                    <Button data-testid="learn-more-chasnell-btn" className="w-full rounded-full bg-forest-800 hover:bg-forest-900 text-cream-100 group py-6">
                      Learn More About The Chasnell Home™
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* Future Homes */}
      <section className="py-20 md:py-28 px-6 bg-cream-200">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <Card className="p-8 md:p-12 bg-cream-100 border border-sage-200 rounded-3xl text-center">
              <div className="inline-flex p-5 rounded-2xl bg-sage-100 text-forest-700 mb-6"><Building2 className="w-10 h-10" /></div>
              <h2 className="font-serif text-3xl md:text-4xl text-forest-800 mb-6">Future Homes</h2>
              <p className="text-lg text-forest-700 leading-relaxed mb-4">
                Additional homes are envisioned as our organization grows thoughtfully over time.
              </p>
              <p className="text-forest-700 leading-relaxed mb-8">
                Families and professionals are welcome to begin a relationship with us now, so we can stay connected
                as new opportunities become available.
              </p>
              <Link to="/placement">
                <Button data-testid="future-homes-cta-btn" className="rounded-full bg-forest-800 hover:bg-forest-900 text-cream-100 px-8 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-transform duration-300">
                  Begin a Conversation
                </Button>
              </Link>
            </Card>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default OurHomes;
